import type { PostSource } from '../types';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { toast, ToastAction } from '@valwin/facto/components';

import { enqueueShareToast, removeShareToast } from './shareToastQueue';
import { InstagramShareLoadingToast } from './ui/InstagramShareLoadingToast';

const SUCCESS_TOAST_DURATION_MS = 5000;

type ToastState = 'success' | 'error';

export function useShareToast() {
  const { t } = useTranslation();

  const start = useCallback((source: PostSource): string => {
    const toastId = enqueueShareToast(source);

    toast.custom(() => <InstagramShareLoadingToast />, {
      id: toastId,
      duration: Infinity,
    });

    return toastId;
  }, []);

  const resolve = useCallback(
    (toastId: string, state: ToastState, onReopen?: () => void) => {
      if (state === 'success') {
        toast.update(toastId, {
          type: 'success',
          title: t('integrations instagram share toast title'),
          description: t(
            'integrations instagram share toast success description'
          ),
          duration: SUCCESS_TOAST_DURATION_MS,
          meta: {},
        });

        return;
      }

      toast.update(toastId, {
        type: 'error',
        title: t('integrations instagram share toast title'),
        description: t('integrations instagram share toast error description'),
        duration: Infinity,
        meta: onReopen
          ? {
              action: (
                <ToastAction
                  onClick={() => {
                    onReopen();
                    toast.dismiss(toastId);
                  }}
                >
                  {t('integrations instagram share toast open editor')}
                </ToastAction>
              ),
            }
          : {},
      });
    },
    [t]
  );

  const drop = useCallback((toastId: string) => {
    removeShareToast(toastId);
  }, []);

  return { start, resolve, drop };
}
